import { Button, Card, CardBody, Col, Container, Label, Row } from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';

import CommonCardHeader from '../../../coreComponents/CommonCardHeader';
import { Primary, Secondary } from '../../../constants';

import AjaxSourcedData from '../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import {
  getExamHistoryTableColumns,
  getExamListTableColumns,
} from '../../../data/formsAndTable/tables/dataTables/DataSource';
import {
  useAssistantCreateExamMutation,
  useAssistantDeleteExamMutation,
  useGetAllExamsQuery,
  useGetAllUsersQuery,
} from '../../../reduxToolkit/reducers/usersSlice';
import { CalendarData } from '../../../types/Applications';
import { useEffect, useMemo, useState } from 'react';
import { baseLang, createCalendarData, getCalendarStops } from '../../../lib/utils';
import { useAppSelector } from '../../../reduxToolkit/hooks';
import CalendarContainer from '../../applications/calendar';
import { UserRole } from '../../../types/Forms';
import { AssistantExamDataCreate, ExamData } from '../../../types/Tables';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import { useTranslation } from 'react-i18next';
import { getPrompts } from '../../../services/api';
import Swal from 'sweetalert2';
import CreateExamForm from '../assistantCreateExam/CreateExamForm';
import { formatWithOffset } from '../../../lib/timeUtils';
import { ExamHistoryItem } from '../../../types/Exams';
import { cleanupBodyLocks } from '../../uiKits/modal/common/CommonModal';
import { useNavigate } from 'react-router-dom';
import { NoDataComponent } from '../../formsAndTable/forms/formControls/NoDataComponent';

export const formDefaultsInit: Partial<AssistantExamDataCreate> = {
  name: '',
  courseId: 0,
  lessonId: 0,
  prompt: { ru: '', uk: '' },
};

const monthRangeIso = (d = new Date(), tz = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const startLocal = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  const endLocal = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start: startLocal.toISOString(), end: endLocal.toISOString() };
};

const ExamListPageContainer = () => {
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const nowIso = new Date().toISOString();
  const { role } = useAppSelector((state) => state.auth);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<'examDate' | 'user'>('examDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [totalRowsPast, setTotalRowsPast] = useState(0);
  const [totalRowsFuture, setTotalRowsFuture] = useState(0);

  const [cursorDate, setCursorDate] = useState(new Date());
  const { start, end } = useMemo(() => monthRangeIso(cursorDate, viewerTz), [cursorDate, viewerTz]);

  const calendarQueryArgs = useMemo(() => {
    return {
      scope: 'calendar' as const,
      start,
      end,
      sortBy: 'examDate',
      sortDir: 'asc',
      limit: 2000,
    };
  }, [start, end]);

  const tableQueryArgs = useMemo(() => {
    return {
      scope: 'table' as const,
      status: 'past',
      page,
      limit,
      search,
      sortBy,
      sortDir,
    };
  }, [page, limit, search, sortBy, sortDir]);

  const tableQueryArgsFuture = useMemo(() => {
    return {
      scope: 'table' as const,
      status: 'future',
      page,
      limit,
      search,
      sortBy,
      sortDir,
    };
  }, [page, limit, search, sortBy, sortDir]);

  const {
    data: examsData,
    refetch: refetchExamsData,
    isLoading: isLoadingCalendar,
    isFetching: isFetchingCalendar,
  } = useGetAllExamsQuery(calendarQueryArgs);
  const {
    data: examsHistoryTableData,
    isFetching: isFetchingHistory,
    isLoading: isLoadingHistory,
  } = useGetAllExamsQuery(tableQueryArgs);
  const {
    data: examsFutureTableData,
    isFetching: isFetchingFuture,
    isLoading: isLoadingFuture,
  } = useGetAllExamsQuery(tableQueryArgsFuture);

  const [calendarData, setCalendarData] = useState<CalendarData[]>([]);

  // const [calendarStopPeriods, setCalendarStopPeriods] = useState([]);

  const [isOpenAddExamForm, setIsOpenAddExamForm] = useState(false);
  const [examTableGroupedData, setExamTableGroupedData] = useState<ExamData[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [userExamList, setUserExamList] = useState<Partial<ExamData>>({});

  const [formDefaults, setFormDefaults] =
    useState<Partial<AssistantExamDataCreate>>(formDefaultsInit);
  const [questionPrompt, setQuesionPrompt] = useState({ ru: '', uk: '' });
  const [createAssistantExam] = useAssistantCreateExamMutation();
  const { t, i18n } = useTranslation();
  const [calendarIsLoading, setCalendarIsLoading] = useState(true);
  const pageLang = baseLang(i18n.language);
  const [deleteExam] = useAssistantDeleteExamMutation();

  const navigate = useNavigate();
  useEffect(() => {
    const getQuestionPrompt = async () => {
      const requests = await getPrompts();
      setQuesionPrompt({ ru: requests[0].questionRequest, uk: requests[0].questionRequest_uk });
    };
    getQuestionPrompt();
  }, []);
  useEffect(() => {
    if (isLoadingCalendar && isFetchingCalendar) setCalendarIsLoading(true);
  }, [isLoadingCalendar, isFetchingCalendar]);
  useEffect(() => {
    if (examsData && examsData.items.length > 0) {
      const items = examsData?.items ?? [];
      setCalendarData(createCalendarData(items));
      const groupedMap = examsData.items.reduce(
        (acc: any, item: any) => {
          const email = item.user.email;
          const { firstName, lastName } = item.user;
          if (!acc[email]) {
            acc[email] = {
              user: { email, firstName, lastName },
              data: [],
            };
          }
          acc[email].data.push(item);
          return acc;
        },
        {} as Record<string, { user: { email: string }; data: typeof examsData.items }>,
      );
      const groupedArray = Object.values(groupedMap) as ExamData[];
      setExamTableGroupedData(groupedArray);
      setCalendarIsLoading(false);
    }
    if (examsData && examsData.items.length === 0) {
      setCalendarIsLoading(false);
      setCalendarData([]);
    }
  }, [examsData]);

  const handleCloseModal = () => {
    setUserExamList({});
    setIsOpenModal(false);
  };

  const deleteExamCalendarHandler = async (data: any) => {
    try {
      const payload = {
        examId: data.examId,
        userId: data.userId,
        examDate: data.examDate,
      };
      const res = await deleteExam(payload).unwrap();

      if (res.success) {
        Swal.fire({
          title: t('courses.exam.calendar.eventDeleteTitle'),
          text: t('courses.exam.calendar.eventDeleteText'),
          icon: 'success',
          confirmButtonColor: Primary,
        });
      } else if (!res.success) {
        Swal.fire({
          title: 'Error!',
          text: 'Exam delete failed',
          icon: 'error',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Exam delete failed',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    }
  };
  const handleOpenAddExamForm = (data: any) => {
    const formDefaults = {
      id: data.userId,
      name: data.userName,
      courseId: data.courseId,
      lessonId: data.lessonId,
      date: data.examDate,
      prompt: questionPrompt,
      parentExamId: data.examId,
    };

    setFormDefaults(formDefaults);
    setIsOpenAddExamForm(true);
  };
  useEffect(() => {
    if (examsHistoryTableData) {
      setTotalRowsPast(examsHistoryTableData.total);
    }
    if (examsFutureTableData) {
      setTotalRowsFuture(examsFutureTableData.total);
    }
  }, [examsHistoryTableData, examsFutureTableData]);

  const handleUnappointedExam = (props: ExamHistoryItem) => {
    Swal.fire({
      title: t('courses.exam.calendar.examPopupTitle'),

      html: `
        <div class="event-popup">
     
          <div class="info-grid">
            <div class="label">${t('courses.exam.calendar.eventCourse')}:</div>
            <div class="value">${props.course.name ?? '—'}</div>
    
             <div class="label">${t('courses.exam.calendar.eventCategory')}:</div>
            <div class="value">${props.lesson.category.name}</div>
    
            <div class="label">${t('courses.exam.calendar.eventLesson')}:</div>
            <div class="value">${props.lesson.title}</div>
      
            <div class="label">${t('courses.exam.calendar.eventDate')}:</div>
            <div class="value">${props.date ? new Date(props.date).toLocaleDateString('uk-UA') : '—'}</div>
      
            <div class="label">${t('courses.exam.calendar.eventTime')}:</div>
            <div class="value">${
              props.date
                ? new Date(props.date).toLocaleTimeString('uk-UA', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'
            }</div>
      
            <div class="label">${t('courses.exam.calendar.eventDescription')}:</div>
            <div class="value">${props.lesson.description}</div>
      
            <div class="label f-w-400">Email:</div>
            <div class="value">${props.user.email}</div>
    
            <div class="label f-w-400">${t('courses.exam.student')}:</div>
            <div class="value">${props.user.firstName} ${props.user.lastName}</div>
    
            <div class="label">${t('courses.exam.examLanguage')}:</div>
            <div class="value">${props.examLang ? props.examLang : t('courses.exam.examLangNotSelected')}</div>
    
          </div>
       
        </div>
      `,
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: Primary,
      confirmButtonText: t('courses.exam.calendar.eventRemoveEvent'),
      cancelButtonText: t('courses.exam.calendar.eventCloseEvent'),
      showDenyButton: true,
      denyButtonText: t('exams.createExam'),
      denyButtonColor: Secondary,
      didOpen: () => {
        const el = Swal.getHtmlContainer();
        if (!el) return;

        // Delegate clicks on router links
        el.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const a = target.closest('a[data-router-link]') as HTMLAnchorElement | null;
          if (!a) return;

          // allow new-tab/middle-clicks to behave normally
          const mouseEvent = e as MouseEvent;
          if (
            mouseEvent.metaKey ||
            mouseEvent.ctrlKey ||
            mouseEvent.shiftKey ||
            mouseEvent.button === 1
          ) {
            return;
          }

          e.preventDefault();
          const href = a.getAttribute('href');

          if (href) {
            navigate(href);
            Swal.close();
          } else if (href) {
            window.location.assign(href);
          }
        });
      },
      willClose: () => {
        cleanupBodyLocks();
      },
    }).then((result) => {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // exam date may be string/Date; normalize & validate
      const examDate = props.date ? new Date(props.date) : null;
      const isValidExamDate = examDate instanceof Date && !isNaN(examDate.getTime());
      const isPast = isValidExamDate ? examDate < startOfToday : true; // treat invalid as past

      if (result.isConfirmed) {
        deleteExamCalendarHandler({
          examId: props.id,
          userId: props.user.id,
          examDate: props.date,
        });
      } else if (result.isDenied) {
        Swal.close();
        requestAnimationFrame(() => {
          if (isPast) {
            Swal.fire({
              title: t('courses.exam.calendar.pastDateTitle') || 'Past date',
              text:
                t('courses.exam.calendar.pastDateText') ||
                'You can add an exam only for today or a future date.',
              icon: 'warning',
              confirmButtonColor: Primary,
            });
            return;
          } else {
            const formDefaults = {
              userId: props.user.id,
              userName: `${props.user.firstName} ${props.user.lastName}`,
              courseId: props.course.id,
              lessonId: props.lesson.id,
              examDate: props.date,
              prompt: questionPrompt,
              examId: props.id,
            };

            handleOpenAddExamForm(formDefaults);
          } // <-- run the callback
        });
      }
      // cancel: do nothing
    });
  };

  return (
    <>
      <Breadcrumbs
        mainTitle={t('courses.exam.examListPageTitle')}
        // parent={t('courses.generalTitle')}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <CalendarContainer
              calendarData={calendarData}
              role={role ?? UserRole.STUDENT}
              actionHandler={deleteExamCalendarHandler}
              openAddForm={handleOpenAddExamForm}
              closeAddForm={() => setIsOpenAddExamForm(false)}
              initialDate={cursorDate}
              onMonthChange={(d) => setCursorDate(d)}
              loading={calendarIsLoading}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="12">
            <Card>
              <CommonCardHeader title={t('courses.exam.examHistoryCardTitle')} tag />
              <CardBody>
                <AjaxSourcedData
                  data={examsHistoryTableData?.items}
                  columns={getExamHistoryTableColumns(t, viewerTz, pageLang, handleUnappointedExam)}
                  pagination={true}
                  paginationServer={true}
                  paginationPerPage={limit}
                  paginationDefaultPage={1}
                  paginationTotalRows={totalRowsPast}
                  onChangePage={(p) => setPage(p)}
                  onChangeRowsPerPage={(newPerPage, p) => {
                    setLimit(newPerPage);
                    setPage(1); // or p, depending on your UX
                  }}
                  /* (optional) server sorting */
                  sortServer
                  onSort={(column, direction) => {
                    // column.selector or column.sortField depending on your column defs
                    const key = (column as any).sortField ?? (column as any).selector;
                    setSortDir(direction);
                    setSortBy(key);
                  }}
                  defaultSortAsc={false}
                  searchServer
                  searchText={search}
                  onSearch={(value) => {
                    setSearch(value);
                    setPage(1);
                  }}
                  noDataComponent={<NoDataComponent />}
                  loading={isLoadingHistory || isFetchingHistory}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col sm="12">
            <Card>
              <CommonCardHeader title={t('courses.exam.examFutureCardTitle')} tag />
              <CardBody>
                <AjaxSourcedData
                  data={examsFutureTableData?.items}
                  columns={getExamHistoryTableColumns(t, viewerTz, pageLang, handleUnappointedExam)}
                  pagination={true}
                  paginationServer={true}
                  paginationPerPage={limit}
                  paginationDefaultPage={1}
                  paginationTotalRows={totalRowsFuture}
                  onChangePage={(p) => setPage(p)}
                  onChangeRowsPerPage={(newPerPage, p) => {
                    setLimit(newPerPage);
                    setPage(1); // or p, depending on your UX
                  }}
                  /* (optional) server sorting */
                  sortServer
                  onSort={(column, direction) => {
                    // column.selector or column.sortField depending on your column defs
                    const key = (column as any).sortField ?? (column as any).selector;
                    setSortDir(direction);
                    setSortBy(key);
                  }}
                  defaultSortAsc={false}
                  searchServer
                  searchText={search}
                  onSearch={(value) => {
                    setSearch(value);
                    setPage(1);
                  }}
                  noDataComponent={<NoDataComponent />}
                  loading={isLoadingFuture || isFetchingFuture}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <SimpleModal isOpen={isOpenModal} close={handleCloseModal}>
        <Card>
          <CardBody>
            {userExamList.data?.length ? (
              userExamList.data.map((el, index) => {
                const language = baseLang(i18n.language);
                const examLanguage = () => {
                  if (!el.examLang) return t('courses.exam.examLangNotSelected');
                  if (el.examLang === 'uk') return t('courses.exam.examLangUkrainian');
                  if (el.examLang === 'ru') return t('courses.exam.examLangRussian');
                };
                const lessonTitleTranslated = el.lesson.translations?.find(
                  (tr) => tr.lang === language,
                );
                const courseTitleTranslated = el.course.translations?.find(
                  (tr) => tr.lang === language,
                );
                return (
                  <div key={index} className="mb-4 border-bottom pb-2">
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">{t('courses.generalLessonName')}:</div>
                      <div className="col-8">
                        {language === 'en' ? el.lesson.title : lessonTitleTranslated?.title}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">{t('courses.generalCourseTitle')}:</div>
                      <div className="col-8">
                        {language === 'en' ? el.course.name : courseTitleTranslated?.name}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">{t('courses.exam.examLanguage')}:</div>
                      <div className="col-8">{examLanguage()}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">{t('global.date')}:</div>
                      <div className="col-8">
                        {formatWithOffset(el.date, viewerTz, undefined)}
                        {/* {new Date(el.date).toLocaleString('uk-UA', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                        })} */}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p>{t('courses.exam.examNoExams')}</p>
            )}
            <Button color="success" onClick={handleCloseModal}>
              {t('global.close')}
            </Button>
          </CardBody>
        </Card>
      </SimpleModal>
      <SimpleModal isOpen={isOpenAddExamForm} close={() => setIsOpenAddExamForm(false)} size={'xl'}>
        <CreateExamForm
          defaults={formDefaults}
          close={() => setIsOpenAddExamForm(false)}
          useDefaults={true}
          createAssistantExam={async (payload) => {
            const res = await createAssistantExam(payload).unwrap();
            await refetchExamsData(); // <- force refresh immediately
            return res;
            // setIsOpenAddExamForm(false);   // close the modal
          }}
        />
      </SimpleModal>
    </>
  );
};
export default ExamListPageContainer;
